import { useEffect, useState } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { Users, UserPlus, Crown, Shield, User, Trash2, Loader2, AlertCircle, Building2 } from "lucide-react";
import { Verified } from "../components/ui/kit";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const roleIcon: Record<string, any> = { owner: Crown, admin: Shield, member: User };

export default function CompanyManage() {
  const [, params] = useRoute("/company/:id/manage");
  const [, navigate] = useLocation();
  const { user, loading } = useAuth();
  const id = params?.id || "";
  const [data, setData] = useState<any>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "forbidden">("loading");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try { const d = await api.companyMembers(id); if (d?.company) { setData(d); setState("ready"); } else setState("notfound"); }
    catch (e: any) { setState(/member/i.test(e?.message || "") ? "forbidden" : "notfound"); }
  }
  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [id, user?.id]);

  if (loading || state === "loading") return <DetailLoading label="Loading company…" />;
  if (state === "notfound") return <DetailNotFound title="Company not found" />;
  if (state === "forbidden") return <DetailNotFound title="You're not a member of this company" backHref="/dashboard" backLabel="Back to Dashboard" />;

  const { company, myRole, members } = data;
  const canManage = myRole === "owner" || myRole === "admin";

  async function invite(e: React.FormEvent) {
    e.preventDefault(); setErr(null);
    if (!email.trim()) { setErr("Enter an email."); return; }
    setBusy("invite");
    try { await api.inviteMember(id, email.trim(), role); setEmail(""); await load(); }
    catch (e: any) { setErr(e?.message || "Could not send the invite."); }
    finally { setBusy(null); }
  }
  async function setMemberRole(m: any, next: "admin" | "member") {
    setBusy(m.id); try { await api.changeMemberRole(id, m.id, next); await load(); } catch (e: any) { setErr(e?.message || "Could not update role."); } finally { setBusy(null); }
  }
  async function remove(m: any) {
    setBusy(m.id + "x"); try { await api.removeMember(id, m.id); await load(); } catch (e: any) { setErr(e?.message || "Could not remove member."); } finally { setBusy(null); }
  }

  return (
    <div className="container-x py-6 max-w-3xl">
      <Link href="/dashboard" className="text-sm text-mut hover:text-navy">← Back to Dashboard</Link>
      <div className="flex items-center gap-3 mt-3 mb-5">
        <div className="w-12 h-12 rounded-lg bg-navy text-white grid place-items-center font-extrabold">{company.initials}</div>
        <div>
          <h1 className="text-2xl font-extrabold text-navy flex items-center gap-2">{company.name}{company.verified && <Verified label="Verified Company" />}</h1>
          <p className="text-sm text-mut flex items-center gap-1"><Users className="w-4 h-4" />{members.length} member{members.length === 1 ? "" : "s"} · you're {myRole}</p>
        </div>
        <Link href={`/c/${company.id}`} className="btn-outline ml-auto h-9"><Building2 className="w-4 h-4" />Public page</Link>
      </div>

      {canManage && (
        <div className="card p-5 mb-5">
          <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><UserPlus className="w-4 h-4" />Invite a member</h3>
          <form onSubmit={invite} className="flex flex-col sm:flex-row gap-2">
            <input className="field flex-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Their KontrataPH account email" />
            <select className="field sm:w-36" value={role} onChange={(e) => setRole(e.target.value as any)}><option value="member">Member</option><option value="admin">Admin</option></select>
            <button type="submit" disabled={busy === "invite"} className="btn-red disabled:opacity-60">{busy === "invite" ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}Invite</button>
          </form>
          {err && <p className="text-sm text-brand-red flex items-center gap-1 mt-2"><AlertCircle className="w-4 h-4" />{err}</p>}
          <p className="text-xs text-mut mt-2">The person must already have a KontrataPH account. They'll get an invite to accept from their dashboard.</p>
        </div>
      )}

      <div className="card p-5">
        <h3 className="font-bold text-navy mb-3">Team</h3>
        <div className="space-y-2">
          {members.map((m: any) => {
            const RIcon = roleIcon[m.role] || User;
            const isOwner = m.role === "owner";
            return (
              <div key={m.id} className="flex items-center justify-between border border-line rounded-lg p-3 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-navy-50 text-navy grid place-items-center text-xs font-bold shrink-0">{(m.userName || "M").slice(0, 1)}</div>
                  <div className="min-w-0">
                    <Link href={`/u/${m.userId}`} className="text-sm font-semibold text-navy hover:text-brand-red truncate block">{m.userName}</Link>
                    <div className="text-xs text-mut truncate">{m.email}{m.status === "invited" ? " · invite pending" : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`chip capitalize ${isOwner ? "bg-brand-gold/15 text-[#8a5a00]" : "bg-navy-50 text-navy-600"}`}><RIcon className="w-3 h-3" />{m.role}</span>
                  {myRole === "owner" && !isOwner && m.status === "active" && (
                    <select className="field h-8 w-24 text-xs" value={m.role} onChange={(e) => setMemberRole(m, e.target.value as any)} disabled={busy === m.id}>
                      <option value="member">Member</option><option value="admin">Admin</option>
                    </select>
                  )}
                  {canManage && !isOwner && (
                    <button onClick={() => remove(m)} disabled={busy === m.id + "x"} className="btn-ghost h-8 w-8 p-0 text-brand-red disabled:opacity-50" title="Remove"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
